<script setup lang="ts">
import { Vector3 } from 'three'
import { onMounted, onBeforeUnmount, ref, watch, watchPostEffect } from 'vue'
import { useTresContext } from '@tresjs/core'
import { use3dPlacement } from '@/composables/use3dPlacement';
import { useTweakpane } from '@/composables/useTweakpane';
import { gsap } from 'gsap';

const cameraRef = ref(null);



const circleRef = ref(null);
const showCircle = ref(true);
let canvas: HTMLCanvasElement | null = null;
const finalPosition = ref([0, 0, 0]);
const finalScale = ref(0.5);
const offsetY = ref(0);

const props = defineProps({
  size: {
    type: Number,
    default: 1.5
  },
  linkedObject: {
    type: String,
    default: '',
  },
  position: {
    type: Array as () => [number, number, number],
    default: () => [0, 1, 0],
  },
  gradient: {
    type: Boolean,
    default: false,
  },
  opacity: {
    type: Number,
    default: 1.0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})


let resizeObserver: ResizeObserver | null = null;
let observedElement: Element | null = null;
let debugFolder: any = null;
let visibilityObserver: IntersectionObserver | null = null;
let observeRetryId: number | null = null;
let refreshHandle: number | null = null;
const deferredTimeouts: number[] = [];
const isLinkedElementVisible = ref(true);

const onLinkedObjectUpdate = () => {
  observeLinkedElement();
  updateCirclePosition();
};

const updateCirclePosition = () => {

  if (!cameraRef.value) return;

  const linkedEl = document.querySelector(props.linkedObject) as HTMLElement;  
  
  if (linkedEl) {
    const { calculate3dPosition } = use3dPlacement();
    
    const y3D = calculate3dPosition(linkedEl, cameraRef.value);

    offsetY.value = y3D;
    finalPosition.value = [props.position[0], offsetY.value + props.position[1], props.position[2]];
    initDebug();

    // Animate fade-in only once after initial positioning
    if (circleRef.value && uniforms.value.uOpacity.value === 0) {
      gsap.to(uniforms.value.uOpacity, {
        value: props.opacity,
        duration: 1,
        ease: 'power4.in',
      });
      
    }
  }
};

defineExpose({
  updateCirclePosition,
});

onMounted(() => {
  cameraRef.value = useTresContext().camera.value;
  showCircle.value = true;
  if (typeof window !== 'undefined') {
    canvas = document.querySelector('#main-canvas') as HTMLCanvasElement | null
  }

  finalPosition.value = props.position;
  finalScale.value = props.size;

  uniforms.value = {
    iTime: { value: 0 },
    iResolution: {
      value: new Vector3(
        canvas?.offsetHeight ?? 1,
        canvas?.offsetHeight ?? 1,
        1
      ),
    },
    hue: { value: 0 },
    rot: { value: 0 },
    uOpacity: { value: 0.0 },
  };

  if (props.linkedObject) {
    window.addEventListener('resize', updateCirclePosition, { passive: true });

    resizeObserver = new ResizeObserver(onLinkedObjectUpdate);
    observeLinkedElement();
  }

  updateCirclePosition();
  schedulePositionRefresh();
});

watch(() => props.linkedObject, () => {
  observeLinkedElement();
  updateCirclePosition();
  schedulePositionRefresh();
});

watch(() => props.opacity, (newVal) => {
  if (circleRef.value?.material.uniforms.uOpacity) {
    gsap.to(circleRef.value.material.uniforms.uOpacity, {
      value: newVal,
      duration: 0.5,
      ease: 'power2.out',
    });
  }
});

watch(() => props.disabled, (value) => {
  if (!circleRef.value?.material?.uniforms?.uOpacity) {
    return;
  }

  const targetOpacity = value ? 0 : props.opacity;
  gsap.to(circleRef.value.material.uniforms.uOpacity, {
    value: targetOpacity,
    duration: value ? 0.3 : 0.5,
    ease: 'power2.out',
  });
});
        

onBeforeUnmount(() => {

  if (circleRef.value) {
    dispose();
  }
  
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
    observedElement = null;
  }

  if (visibilityObserver) {
    visibilityObserver.disconnect();
    visibilityObserver = null;
  }

  if (observeRetryId !== null) {
    window.clearTimeout(observeRetryId);
    observeRetryId = null;
  }

  if (refreshHandle !== null) {
    window.cancelAnimationFrame(refreshHandle);
    refreshHandle = null;
  }

  for (const id of deferredTimeouts.splice(0)) {
    window.clearTimeout(id);
  }
  
  window.removeEventListener('resize',updateCirclePosition);
  showCircle.value = false;
  if (typeof unsubscribeLoop === 'function') {
    unsubscribeLoop();
  } else if (unsubscribeLoop && typeof unsubscribeLoop.stop === 'function') {
    unsubscribeLoop.stop();
  }

  if (debugFolder) {
    debugFolder.dispose();
    debugFolder = null;
  }

}); 

const updatePosition = () => {
  
  showCircle.value = false;
 const {camera} = useTresContext()

  if (!cameraRef.value) {
    console.warn('Camera reference is not set.');
    return;
  }

  const linkedElement = document.querySelector(props.linkedObject + ' .booking-generating');
  if (!linkedElement) {
    console.warn('Linked element not found:', props.linkedObject);
    return;
  }

 const { calculate3dPosition } = use3dPlacement(); 

  if (props.linkedObject) {
      const linkedEl = document.querySelector(props.linkedObject) as HTMLElement;
      if (linkedEl) {        
        offsetY.value = calculate3dPosition(linkedEl, cameraRef.value );
        setTimeout(() => {
          finalPosition.value = [props.position[0], offsetY.value + props.position[1], props.position[2]];
        }, 1000)
      }
   }

   showCircle.value = true;
   schedulePositionRefresh();
}

const schedulePositionRefresh = (attempts = 10) => {
  if (typeof window === 'undefined') return;
  if (refreshHandle !== null) {
    window.cancelAnimationFrame(refreshHandle);
    refreshHandle = null;
  }

  for (const id of deferredTimeouts.splice(0)) {
    window.clearTimeout(id);
  }

  const run = (remaining: number) => {
    if (remaining <= 0) return;
    refreshHandle = window.requestAnimationFrame(() => {
      updateCirclePosition();
      run(remaining - 1);
    });
  };

  run(attempts);

  deferredTimeouts.push(
    window.setTimeout(() => {
      updateCirclePosition();
    }, 300)
  );

  deferredTimeouts.push(
    window.setTimeout(() => {
      updateCirclePosition();
    }, 800)
  );
};

const uniforms = ref({
  iTime: { value: 0 },
  iResolution: { value: new Vector3(0, 0, 0) },
  hue: { value: 0 },
  rot: { value: 0 },
  uOpacity: { value: 0.0 },
})

const fragmentShader = /* glsl */ `
    precision highp float;

    uniform float iTime;
    uniform vec3 iResolution;
    uniform float hue;
    uniform float rot;
    uniform float uOpacity;
    varying vec2 vUv;

    vec3 rgb2yiq(vec3 c) {
      float y = dot(c, vec3(0.299, 0.587, 0.114));
      float i = dot(c, vec3(0.596, -0.274, -0.322));
      float q = dot(c, vec3(0.211, -0.523, 0.312));
      return vec3(y, i, q);
    }
    
    vec3 yiq2rgb(vec3 c) {
      float r = c.x + 0.956 * c.y + 0.621 * c.z;
      float g = c.x - 0.272 * c.y - 0.647 * c.z;
      float b = c.x - 1.106 * c.y + 1.703 * c.z;
      return vec3(r, g, b);
    }
    
    vec3 adjustHue(vec3 color, float hueDeg) {
      float hueRad = hueDeg * 3.14159265 / 180.0;
      vec3 yiq = rgb2yiq(color);
      float cosA = cos(hueRad);
      float sinA = sin(hueRad);
      float i = yiq.y * cosA - yiq.z * sinA;
      float q = yiq.y * sinA + yiq.z * cosA;
      yiq.y = i;
      yiq.z = q;
      return yiq2rgb(yiq);
    }

    vec3 hash33(vec3 p3) {
      p3 = fract(p3 * vec3(0.1031, 0.11369, 0.13787));
      p3 += dot(p3, p3.yxz + 19.19);
      return -1.0 + 2.0 * fract(vec3(
        p3.x + p3.y,
        p3.x + p3.z,
        p3.y + p3.z
      ) * p3.zyx);
    }

    float snoise3(vec3 p) {
      const float K1 = 0.333333333;
      const float K2 = 0.166666667;
      vec3 i = floor(p + (p.x + p.y + p.z) * K1);
      vec3 d0 = p - (i - (i.x + i.y + i.z) * K2);
      vec3 e = step(vec3(0.0), d0 - d0.yzx);
      vec3 i1 = e * (1.0 - e.zxy);
      vec3 i2 = 1.0 - e.zxy * (1.0 - e);
      vec3 d1 = d0 - (i1 - K2);
      vec3 d2 = d0 - (i2 - K1);
      vec3 d3 = d0 - 0.5;
      vec4 h = max(0.6 - vec4(
        dot(d0, d0),
        dot(d1, d1),
        dot(d2, d2),
        dot(d3, d3)
      ), 0.0);
      vec4 n = h * h * h * h * vec4(
        dot(d0, hash33(i)),
        dot(d1, hash33(i + i1)),
        dot(d2, hash33(i + i2)),
        dot(d3, hash33(i + 1.0))
      );
      return dot(vec4(31.316), n);
    }

    vec4 extractAlpha(vec3 colorIn) {
      float a = max(max(colorIn.r, colorIn.g), colorIn.b);
      return vec4(colorIn.rgb / (a + 1e-5), a);
    }

    const vec3 baseColor1 = vec3(0.611765, 0.262745, 0.996078);
    const vec3 baseColor2 = vec3(0.298039, 0.760784, 0.913725);
    const vec3 baseColor3 = vec3(0.062745, 0.078431, 0.600000);
    const float innerRadius = 0.6;
    const float noiseScale = 0.65;

    float light1(float intensity, float attenuation, float dist) {
      return intensity / (1.0 + dist * attenuation);
    }
    float light2(float intensity, float attenuation, float dist) {
      return intensity / (1.0 + dist * dist * attenuation);
    }

    vec4 draw(vec2 uv) {
      vec3 color1 = adjustHue(baseColor1, hue);
      vec3 color2 = adjustHue(baseColor2, hue);
      vec3 color3 = adjustHue(baseColor3, hue);
      
      float ang = atan(uv.y, uv.x);
      float len = length(uv);
      float invLen = len > 0.0 ? 1.0 / len : 0.0;
      
      float n0 = snoise3(vec3(uv * noiseScale, iTime * 0.5)) * 0.5 + 0.5;
      float r0 = mix(mix(innerRadius, 1.0, 0.4), mix(innerRadius, 1.0, 0.6), n0);
      float d0 = distance(uv, (r0 * invLen) * uv);
      float v0 = light1(1.0, 10.0, d0);
      v0 *= smoothstep(r0 * 1.05, r0, len);
      float cl = cos(ang + iTime * 2.0) * 0.5 + 0.5;
      
      float a = iTime * -1.0;
      vec2 pos = vec2(cos(a), sin(a)) * r0;
      float d = distance(uv, pos);
      float v1 = light2(1.5, 5.0, d);
      v1 *= light1(1.0, 50.0, d0);
      
      float v2 = smoothstep(1.0, mix(innerRadius, 1.0, n0 * 0.5), len);
      float v3 = smoothstep(innerRadius, mix(innerRadius, 1.0, 0.5), len);
      
      vec3 col = mix(color1, color2, cl);
      col = mix(color3, col, v0);
      col = (col + v1) * v2 * v3;
      col = clamp(col, 0.0, 1.0);
      
      return extractAlpha(col);
    }

    vec4 mainImage(vec2 fragCoord) {
      vec2 center = iResolution.xy * 0.5;
      float size = min(iResolution.x, iResolution.y);
      vec2 uv = (fragCoord - center) / size * 2.0;
      
      float angle = rot;
      float s = sin(angle);
      float c = cos(angle);
      uv = vec2(c * uv.x - s * uv.y, s * uv.x + c * uv.y);

      vec4 color = draw(uv);

      ${
          props.gradient ? 
            /* glsl */ `float gradient = clamp(vUv.y * 2.0 - 0.35, 0.0, 1.0);

            color.a *= gradient;
            color.rgb *= color.a; `
          :
            ''}
      
      return color;
    }

    void main() {
      vec2 fragCoord = vUv * iResolution.xy;
      vec4 col = mainImage(fragCoord);
    //   gl_FragColor = vec4(col.rgb * col.a, col.a);
    gl_FragColor = vec4(col.rgb * col.a * uOpacity, col.a * uOpacity);
    }
  `;

const vertexShader = /* glsl */ `
    uniform vec2 uAmplitude;
    uniform vec2 uFrequency;
    uniform float uTime;

    varying vec2 vUv;

    void main() {
        vec4 modelPosition = modelMatrix * vec4(position, 1.0);
        vec4 viewPosition = viewMatrix * modelPosition;
        gl_Position = projectionMatrix * viewPosition;
        vUv = uv;
    }
`
import { useRenderLoop } from '@tresjs/core'
import { log } from 'three/tsl';
const hue = ref(0)
let unsubscribeLoop: any = null;
let lastHueUpdate = 0;
const targetFrameInterval = 1 / 30;
unsubscribeLoop = useRenderLoop().onLoop(({ delta, elapsed }) => {
    if (!circleRef.value || !circleRef.value.material || !isLinkedElementVisible.value || props.disabled) {
        return;
    }
    if (elapsed - lastHueUpdate < targetFrameInterval) {
        return;
    }
    lastHueUpdate = elapsed;

    const program = circleRef.value.material
    program.uniforms.iTime.value = elapsed
    program.uniforms.hue.value = hue.value
    hue.value += delta * 0.1
    if (hue.value > 360) hue.value -= 360
})

const initDebug = () => {
  if (!window.location.hash.includes('debug')) {
    return;
  }

  if (debugFolder) {
    return;
  }

  const pane = useTweakpane();

  // Crée un dossier unique pour cette instance de rocher
  debugFolder = pane.addFolder({ title: `IlluminatedCircle` });

  // --- Scale Control ---
  debugFolder.addBinding(finalScale, 'value', { label: 'Scale', min: 0.01, max: 5, step: 0.01 });

  // --- Position Controls (X, Y, Z Sliders) ---
  const posObject = { 
    get x() { return finalPosition.value[0]; }, set x(v) { finalPosition.value = [v, finalPosition.value[1], finalPosition.value[2]]; },
    get y() { return finalPosition.value[1] - offsetY.value; }, set y(v) { finalPosition.value = [finalPosition.value[0], v + offsetY.value, finalPosition.value[2]]; },
    get z() { return finalPosition.value[2]; }, set z(v) { finalPosition.value = [finalPosition.value[0], finalPosition.value[1], v]; }
  };
  debugFolder.addBinding(posObject, 'x', { label: 'Pos X', min: -10, max: 10, step: 0.1 });
  debugFolder.addBinding(posObject, 'y', { label: 'Pos Y', min: -10, max: 10, step: 0.1 });
  debugFolder.addBinding(posObject, 'z', { label: 'Pos Z', min: -10, max: 10, step: 0.1 });
}



function dispose(){
    // Récupère l'objet 3D
    const mesh = circleRef.value

    // Le retire de la scène
    if (mesh.parent) {
      mesh.parent.remove(mesh)
    }

    // Dispose des ressources
    mesh.geometry?.dispose()
    mesh.material?.dispose?.()
}

function observeLinkedElement() {
  if (!resizeObserver || !props.linkedObject) return;
  const linkedEl = document.querySelector(props.linkedObject);

  if (observedElement && observedElement !== linkedEl) {
    resizeObserver.unobserve(observedElement);
    observedElement = null;
  }

  if (linkedEl && observedElement !== linkedEl) {
    resizeObserver.observe(linkedEl);
    observedElement = linkedEl;
  }

  if (visibilityObserver) {
    visibilityObserver.disconnect();
    visibilityObserver = null;
  }

  if (linkedEl) {
    if (observeRetryId !== null) {
      window.clearTimeout(observeRetryId);
      observeRetryId = null;
    }
    visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      isLinkedElementVisible.value = entry ? entry.isIntersecting : true;
    }, { threshold: 0.15 });
    visibilityObserver.observe(linkedEl);
    updateCirclePosition();
    schedulePositionRefresh();
  } else {
    isLinkedElementVisible.value = true;
    if (typeof window !== 'undefined') {
      observeRetryId = window.setTimeout(observeLinkedElement, 200);
    }
  }
}

</script>

<template>
    <TresMesh
      v-if="showCircle"
      ref="circleRef"
      :position="finalPosition"
      >
      <TresPlaneGeometry :args="[finalScale, finalScale]" />
      <TresShaderMaterial :vertex-shader="vertexShader" :fragment-shader="fragmentShader" :uniforms="uniforms" :transparent="true" />
    </TresMesh>

</template>
